import { sectionRenderer } from '@theme/section-renderer';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { startViewTransition } from '@theme/utilities';

// Legacy mode only. Where the gendered size metafields haven't been backfilled
// the facet still carries the raw stored size — the men's value on unisex
// products — and a women's size is 2 larger (mirrors fluevog-size-picker.js on
// the PDP). In facet mode each gender has its own facet whose values are
// already the right numbers, so nothing below touches them.
const WOMEN_OFFSET = 2;

// The gender the shopper is browsing in, carried across the collection -> PDP
// hop (assets/fluevog-size-picker.js reads the same key). sessionStorage throws
// in Safari private mode, so every access is guarded.
const GENDER_STORAGE_KEY = 'fv:size-gender';

/** @returns {'women' | 'men' | null} */
function readStoredGender() {
  try {
    const stored = sessionStorage.getItem(GENDER_STORAGE_KEY);
    return stored === 'women' || stored === 'men' ? stored : null;
  } catch {
    return null;
  }
}

/** @param {'women' | 'men'} gender */
function writeStoredGender(gender) {
  try {
    sessionStorage.setItem(GENDER_STORAGE_KEY, gender);
  } catch {
    // Storage unavailable — the gender simply doesn't carry over to the PDP.
  }
}

/**
 * Format a raw numeric size for display, applying the gender offset. Half sizes
 * are shown with ½ to match the Liquid-rendered labels.
 * @param {string} base
 * @param {number} offset
 * @returns {string | null}
 */
function formatSize(base, offset) {
  const num = parseFloat(base);
  if (isNaN(num)) return null;
  const val = Math.round((num + offset) * 10) / 10;
  return val.toString().replace('.5', '½');
}

/**
 * Same offset math as formatSize, but plain decimal notation (no ½) to match
 * how the native filter drawer already renders raw Shopify facet labels.
 * @param {string} base
 * @param {number} offset
 * @returns {string | null}
 */
function formatSizePlain(base, offset) {
  const num = parseFloat(base);
  if (isNaN(num)) return null;
  const val = Math.round((num + offset) * 10) / 10;
  return val.toString();
}

/**
 * Progressive-enhancement wrapper around the Fluevog collection filters
 * (size circles + active-filter chips + clear all + sort).
 *
 * Every control is a real <a> facet link (url_to_add / url_to_remove), so it
 * works as a full page reload with no JS. When JS is available we intercept the
 * click and update only the product grid + this toolbar via the Section
 * Rendering API, matching Horizon's native AJAX filtering.
 *
 * The toolbar also listens for ThemeEvents.FilterUpdate so chips / size circles
 * stay in sync when filters change from the #filters-drawer facets form
 * (which only re-renders the main-collection section on its own).
 */
class FvCollectionFilters extends HTMLElement {
  /** @type {boolean} */
  static skipNextToolbarRender = false;

  /** @type {'women' | 'men'} Remembered across section re-renders. */
  static activeGender = 'men';

  /** @type {boolean} True once the shopper has toggled gender this session. */
  static genderChosen = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);

    if (!FvCollectionFilters.listenersBound) {
      window.addEventListener('popstate', FvCollectionFilters.handlePopState);
      document.addEventListener(ThemeEvents.FilterUpdate, FvCollectionFilters.handleFilterUpdate);
      FvCollectionFilters.listenersBound = true;
    }

    FvCollectionFilters.activeGender = FvCollectionFilters.#resolveGender(this);
    this.#applyGender(FvCollectionFilters.activeGender);
  }

  /**
   * The gender the URL is actually filtered by. The drawer's "Women's Size" /
   * "Men's Size" checkboxes apply a facet without going anywhere near this
   * toolbar, so on an ungendered collection the URL is the only place that
   * choice shows up.
   * @param {HTMLElement} toolbar
   * @returns {'women' | 'men' | null}
   */
  static #genderFromUrl(toolbar) {
    const params = new URLSearchParams(window.location.search);
    const womenParam = toolbar.dataset.fvParamWomen;
    const menParam = toolbar.dataset.fvParamMen;

    const hasWomen = womenParam ? params.has(womenParam) : false;
    const hasMen = menParam ? params.has(menParam) : false;

    // Both applied (hand-edited URL, or a stale link) — no signal either way,
    // so leave whatever is already active alone.
    if (hasWomen && hasMen) return FvCollectionFilters.activeGender;
    if (hasWomen) return 'women';
    if (hasMen) return 'men';
    return null;
  }

  /**
   * Gender precedence, highest first: a women's / men's collection, then an
   * applied gendered size facet, then the shopper's toggle this page-load, then
   * what they last chose this session, then the server-rendered default.
   *
   * The first two are facts about what the shopper is looking at rather than
   * preferences, so they also refresh the remembered context for the PDP. They
   * sit above the toggle because a toggle with a size filter applied navigates
   * (see #translateSizeFilter), which puts the URL in agreement anyway.
   * @param {HTMLElement} toolbar
   * @returns {'women' | 'men'}
   */
  static #resolveGender(toolbar) {
    const serverGender = toolbar.dataset.fvGender === 'women' ? 'women' : 'men';

    // No toggle renders on a locked collection, so genderChosen is never set there.
    if (toolbar.hasAttribute('data-fv-gender-locked')) {
      writeStoredGender(serverGender);
      return serverGender;
    }

    const fromUrl = FvCollectionFilters.#genderFromUrl(toolbar);
    if (fromUrl) {
      writeStoredGender(fromUrl);
      return fromUrl;
    }

    if (FvCollectionFilters.genderChosen) return FvCollectionFilters.activeGender;
    return readStoredGender() ?? serverGender;
  }

  /**
   * Toggle the WOMEN / MEN buttons, then sync the size circles.
   *
   * In facet mode both gendered rows are already in the DOM with correct
   * labels — only their visibility changes. In legacy mode there is a single
   * row of raw stored sizes, relabelled here as the stored value plus 2 for
   * women; the underlying filter links are unchanged.
   * @param {'women' | 'men'} gender
   */
  #applyGender(gender) {
    this.querySelectorAll('.fv-sizebar__gender').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const active = button.dataset.gender === gender;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (this.dataset.fvSizeMode === 'facet') {
      const groups = this.querySelectorAll('[data-fv-size-group]');
      let matched = false;

      groups.forEach((group) => {
        if (!(group instanceof HTMLElement)) return;
        const active = group.dataset.fvSizeGroup === gender;
        if (active) matched = true;
        group.classList.toggle('is-active', active);
      });

      // Only the other gender's facet has values (a men's-only category, say) —
      // show that row rather than an empty size bar.
      if (!matched && groups[0] instanceof HTMLElement) groups[0].classList.add('is-active');

      FvCollectionFilters.#syncFacets(gender);
      return;
    }

    const offset = gender === 'women' ? WOMEN_OFFSET : 0;

    this.querySelectorAll('.fv-size-chip').forEach((chip) => {
      if (!(chip instanceof HTMLElement)) return;
      const textEl = chip.querySelector('.fv-size-chip__text');
      if (!textEl) return;
      let base = chip.getAttribute('data-fv-size');
      if (base == null) base = (textEl.textContent ?? '').trim().replace('½', '.5');
      const next = formatSize(base, offset);
      if (next !== null) textEl.textContent = next;
    });

    // Active-filter chip for the Size facet (e.g. "Size 12" in the toolbar's
    // chips row) — relabel the same way as the size circles above.
    this.querySelectorAll('.fv-chip__size-value').forEach((valueEl) => {
      if (!(valueEl instanceof HTMLElement)) return;
      const base = valueEl.getAttribute('data-fv-size');
      if (base == null) return;
      const next = formatSize(base, offset);
      if (next !== null) valueEl.textContent = next;
    });

    FvCollectionFilters.#syncFacets(gender);
  }

  /**
   * Sync the Size facet(s) inside the native filter drawer/bar, which live in
   * the product grid section rather than inside this toolbar element.
   *
   * The drawer is re-rendered with the grid, so this also runs off the grid's
   * render promise — and re-resolves the gender first, because that promise and
   * the toolbar's own re-render land in no fixed order and the later one wins.
   */
  static #resyncFacets() {
    const toolbar = document.querySelector('fv-collection-filters');
    if (!(toolbar instanceof HTMLElement)) return;
    FvCollectionFilters.activeGender = FvCollectionFilters.#resolveGender(toolbar);
    FvCollectionFilters.#syncFacets(FvCollectionFilters.activeGender);
  }

  /**
   * @param {'women' | 'men'} gender
   */
  static #syncFacets(gender) {
    const toolbar = document.querySelector('fv-collection-filters');
    if (toolbar instanceof HTMLElement && toolbar.dataset.fvSizeMode === 'facet') {
      FvCollectionFilters.#showActiveFacetSizes(toolbar, gender);
    } else {
      FvCollectionFilters.#relabelFacetSizes(gender);
    }
  }

  /**
   * Liquid can't read the shopper's toggle (no query param carries it), so on an
   * ambiguous surface both gendered size facets are rendered server-side. Show
   * the active one and hide the other. A gendered collection only ever renders
   * one — fluevog-skip-filter.liquid drops the other server-side.
   * @param {HTMLElement} toolbar
   * @param {'women' | 'men'} gender
   */
  static #showActiveFacetSizes(toolbar, gender) {
    /** @type {Array<'women' | 'men'>} */
    const genders = ['women', 'men'];
    /** @type {Record<string, Element | null>} */
    const facets = {};

    for (const candidate of genders) {
      const param = candidate === 'women' ? toolbar.dataset.fvParamWomen : toolbar.dataset.fvParamMen;
      if (!param) continue;
      // list-filter.liquid stamps the param name with dots swapped for hyphens.
      const attr = param.replace(/\./g, '-');
      facets[candidate] = document.querySelector(`accordion-custom.facets__item[data-filter-param-name="${attr}"]`);
    }

    // The active gender has no facet here (single-gender category, or a
    // gendered collection where Liquid already dropped the other one) — leave
    // whatever is rendered visible rather than hiding the only size filter.
    if (!facets[gender]) return;

    for (const candidate of genders) {
      const facet = facets[candidate];
      if (facet instanceof HTMLElement) facet.style.display = candidate === gender ? '' : 'none';
    }
  }

  /**
   * Legacy mode. Relabel the Size facet's values inside the native filter drawer/bar
   * (blocks/filters.liquid + snippets/list-filter.liquid), which lives in the
   * product grid section rather than inside this toolbar element. Both the
   * pill and checkbox render paths keep the raw Shopify value on a
   * `data-label` attribute and place the visible label right after the input,
   * so no Liquid changes are needed to find and relabel it here.
   * @param {'women' | 'men'} gender
   */
  static #relabelFacetSizes(gender) {
    const offset = gender === 'women' ? WOMEN_OFFSET : 0;

    document.querySelectorAll('accordion-custom.facets__item').forEach((accordion) => {
      const filterLabel = accordion.querySelector('.facets__summary .facets__label');
      if (!filterLabel || !filterLabel.textContent?.toLowerCase().includes('size')) return;

      accordion.querySelectorAll('[data-label]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        const label = input.nextElementSibling;
        if (!(label instanceof HTMLLabelElement)) return;

        const next = formatSizePlain(input.dataset.label ?? '', offset);
        if (next === null) return;

        const textEl = label.querySelector('.checkbox__label-text');
        if (textEl) {
          textEl.textContent = next;
          return;
        }

        const textNode = Array.from(label.childNodes).find(
          (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim() !== ''
        );
        if (textNode) textNode.textContent = next;
      });
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @returns {string} The product grid (results-list) section id */
  get gridSectionId() {
    return document.querySelector('results-list')?.getAttribute('section-id') ?? '';
  }

  /** @returns {string} This toolbar's section id */
  get sectionId() {
    return this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '') ?? '';
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const genderBtn = target.closest('.fv-sizebar__gender');
    if (genderBtn instanceof HTMLElement && genderBtn.dataset.gender) {
      event.preventDefault();
      const previous = FvCollectionFilters.activeGender;
      const next = genderBtn.dataset.gender === 'men' ? 'men' : 'women';
      FvCollectionFilters.activeGender = next;
      FvCollectionFilters.genderChosen = true;
      writeStoredGender(next);

      // The two genders are different facet params, so an active size filter has
      // to move across rather than just be relabelled. With nothing filtered yet
      // there's nothing to navigate to — just swap which row is on show.
      if (next !== previous && this.dataset.fvSizeMode === 'facet') {
        const translated = this.#translateSizeFilter(previous, next);
        if (translated) {
          FvCollectionFilters.navigate(translated, this.gridSectionId);
          return;
        }
      }

      this.#applyGender(next);
      return;
    }

    const sortOption = target.closest('[data-fv-sort]');
    if (sortOption instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#applySort(sortOption);
      return;
    }

    const link = target.closest('a[data-fv-ajax]');
    if (!(link instanceof HTMLAnchorElement) || link.classList.contains('is-disabled')) return;

    // Respect modifier clicks (open in new tab, etc.)
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const href = link.getAttribute('href');
    if (!href) return;

    event.preventDefault();
    FvCollectionFilters.navigate(href, this.gridSectionId);
  };

  /**
   * Move an active size filter from one gender's facet param to the other's,
   * converting the value (a women's size is 2 larger than the men's for the same
   * shoe). Returns null when no size filter is active, in which case the toggle
   * stays display-only.
   *
   * A women's-only product carries no men's size at all, so it correctly drops
   * out of the results rather than being shown under a size it isn't sold in.
   * @param {'women' | 'men'} from
   * @param {'women' | 'men'} to
   * @returns {string | null}
   */
  #translateSizeFilter(from, to) {
    const fromParam = from === 'women' ? this.dataset.fvParamWomen : this.dataset.fvParamMen;
    const toParam = to === 'women' ? this.dataset.fvParamWomen : this.dataset.fvParamMen;
    if (!fromParam || !toParam) return null;

    const url = new URL(window.location.href);
    const values = url.searchParams.getAll(fromParam);
    if (values.length === 0) return null;

    const offset = to === 'women' ? WOMEN_OFFSET : -WOMEN_OFFSET;
    url.searchParams.delete(fromParam);

    for (const value of values) {
      const num = parseFloat(value);
      if (isNaN(num)) continue;
      const converted = Math.round((num + offset) * 10) / 10;
      if (converted > 0) url.searchParams.append(toParam, converted.toString());
    }

    url.searchParams.delete('page');
    return url.toString();
  }

  /**
   * Closes the sort menu, syncs the label, then updates the grid.
   * Skips the toolbar section remorph so the caret close animation isn't interrupted.
   * @param {HTMLElement} option
   */
  #applySort(option) {
    const sortValue = option.dataset.fvSort ?? '';
    if (!sortValue) return;

    const details = option.closest('details.fv-sort');
    if (details instanceof HTMLDetailsElement) {
      // Snap closed instantly — a mid-spin caret would get frozen in the
      // product-grid view-transition snapshot and look stuck for ~1s.
      details.classList.add('fv-sort--instant');
      details.open = false;
      // Force a paint of the closed/down caret before startViewTransition runs.
      void details.offsetHeight;
      details.classList.remove('fv-sort--instant');
    }

    this.querySelectorAll('[data-fv-sort]').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const active = btn.dataset.fvSort === sortValue;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    const valueEl = this.querySelector('.fv-sort__value');
    if (valueEl) {
      valueEl.textContent = option.textContent?.trim() ?? '';
    }

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    url.searchParams.delete('page');

    history.pushState({ fvFilter: true }, '', url.toString());

    // Grid/facets need FilterUpdate; toolbar UI is already synced above.
    FvCollectionFilters.skipNextToolbarRender = true;
    document.dispatchEvent(new FilterUpdateEvent(new URLSearchParams(window.location.search)));

    // No product-grid view transition: it screenshots the whole page and freezes
    // the caret if it were mid-rotate (or still looks like a hitch even after snap-close).
    const gridSectionId = this.gridSectionId;
    if (gridSectionId) {
      sectionRenderer.renderSection(gridSectionId).then(() => {
        FvCollectionFilters.#resyncFacets();
      });
    }
  }

  /**
   * Pushes the new URL and re-renders the grid + toolbar.
   * @param {string} href
   * @param {string} gridSectionId
   */
  static navigate(href, gridSectionId) {
    history.pushState({ fvFilter: true }, '', href);
    FvCollectionFilters.render(gridSectionId);
  }

  /**
   * @param {string} gridSectionId
   */
  static render(gridSectionId) {
    const params = new URLSearchParams(window.location.search);
    // Clears PaginatedList page cache / clear-all UI, and re-renders the toolbar
    // via handleFilterUpdate.
    document.dispatchEvent(new FilterUpdateEvent(params));

    if (gridSectionId) {
      // Captured separately from the startViewTransition call below: on
      // browsers without View Transitions support, startViewTransition's
      // fallback path doesn't await the callback's returned promise, so
      // chaining off it directly would run before the morph actually lands.
      const renderPromise = sectionRenderer.renderSection(gridSectionId);
      startViewTransition(() => renderPromise, ['product-grid']);
      renderPromise.then(() => {
        FvCollectionFilters.#resyncFacets();
      });
    }
  }

  /**
   * Re-render the toolbar whenever any facet filter update fires (drawer form,
   * size chips, clear all, or browser back/forward via render()).
   * Skipped after an in-toolbar sort selection so the caret can finish closing.
   */
  static handleFilterUpdate = () => {
    if (FvCollectionFilters.skipNextToolbarRender) {
      FvCollectionFilters.skipNextToolbarRender = false;
      return;
    }

    const el = document.querySelector('fv-collection-filters');
    if (!(el instanceof FvCollectionFilters)) return;

    const toolbarSectionId = el.sectionId;
    if (!toolbarSectionId) return;

    // renderSection morphs the toolbar's existing DOM in place rather than
    // replacing the element, so connectedCallback never re-fires here. Without
    // this, the freshly-morphed markup (always server-rendered off the
    // women's-default section setting) would silently overwrite the
    // shopper's chosen gender/size labels on every filter change.
    sectionRenderer.renderSection(toolbarSectionId, { cache: false }).then(() => {
      const refreshedEl = document.querySelector('fv-collection-filters');
      if (refreshedEl instanceof FvCollectionFilters) {
        // Re-resolved rather than reused: the update may have come from the
        // drawer applying the other gender's size facet.
        FvCollectionFilters.activeGender = FvCollectionFilters.#resolveGender(refreshedEl);
        refreshedEl.#applyGender(FvCollectionFilters.activeGender);
      }
    });
  };

  static handlePopState = () => {
    const el = document.querySelector('fv-collection-filters');
    if (!(el instanceof FvCollectionFilters)) return;
    FvCollectionFilters.render(el.gridSectionId);
  };
}

if (!customElements.get('fv-collection-filters')) {
  customElements.define('fv-collection-filters', FvCollectionFilters);
}
